import { match } from "ts-pattern";
import { c, t } from "ttag";

import type { RevisionOrModerationEvent } from "metabase/plugins";
import type { IconProps } from "metabase/ui";
import { getRelativeTime } from "metabase/utils/time-dayjs";
import type {
  BaseUser,
  CardType,
  ModerationReview,
  UserId,
} from "metabase-types/api";

/**
 * The review that holds an item's current verification state, or `undefined`
 * when nobody has ever verified the item.
 */
export function getLatestModerationReview(
  reviews: readonly ModerationReview[] | undefined,
): ModerationReview | undefined {
  return reviews?.find((review) => review.most_recent);
}

/** The review that verified the item, or `undefined` if it isn't verified. */
export function getVerifiedReview(
  reviews: readonly ModerationReview[] | undefined,
): ModerationReview | undefined {
  const review = getLatestModerationReview(reviews);
  return review?.status === "verified" ? review : undefined;
}

export function isVerified(
  reviews: readonly ModerationReview[] | undefined,
): boolean {
  return getVerifiedReview(reviews) !== undefined;
}

export function isOfficialCollection(collection: {
  authority_level?: string | null;
}): boolean {
  return collection.authority_level === "official";
}

/** "You verified this 3 days ago" or "Verified by Ana Ruiz 3 days ago". */
export function getVerifiedByText(
  review: ModerationReview,
  currentUserId: UserId | undefined,
): string {
  const time = getRelativeTime(review.created_at);

  if (review.moderator_id === currentUserId) {
    return c("{0} is a relative time, such as '3 days ago'")
      .t`You verified this ${time}`;
  }

  const name = review.user.common_name;
  return c("{0} is a person's name, {1} a time such as '3 days ago'")
    .t`Verified by ${name} ${time}`;
}

export function getVerifyCardLabel(type: CardType): string {
  return match(type)
    .with("question", () => t`Verify this question`)
    .with("model", () => t`Verify this model`)
    .with("metric", () => t`Verify this metric`)
    .exhaustive();
}

export function getStatusIcon(status?: string): IconProps | undefined {
  return status === "verified"
    ? { name: "verified", color: "core-brand" }
    : undefined;
}

function getModerationEventTitle(
  review: ModerationReview,
  currentUser: BaseUser | null,
): string {
  const isCurrentUser = review.moderator_id === currentUser?.id;
  const name = review.user.common_name;

  if (review.status === "verified") {
    return isCurrentUser
      ? t`You verified this`
      : c("{0} is the name of a person").t`${name} verified this`;
  }

  return isCurrentUser
    ? t`You removed its verification`
    : c("{0} is the name of a person").t`${name} removed its verification`;
}

/** Verified and Removed-verification events for an item's history timeline. */
export function getModerationTimelineEvents(
  reviews: readonly ModerationReview[] | undefined,
  currentUser: BaseUser | null,
): RevisionOrModerationEvent[] {
  return (reviews ?? []).map(
    (review): RevisionOrModerationEvent => ({
      title: getModerationEventTitle(review, currentUser),
      timestamp: review.created_at,
      icon:
        review.status === "verified"
          ? { name: "verified", color: "core-brand" }
          : "close",
    }),
  );
}
